import { useMemo, useState } from "react";
import * as R from "ramda";
import { arrayOf, bool, shape, string } from "prop-types";
import cx from "classnames";

import { extent } from "d3-array";
import { scaleTime } from "d3-scale";
import parseISO from "date-fns/fp/parseISO";
import addYears from "date-fns/fp/addYears";

import {
  indicatorDataShape,
  getDataByLocation,
} from "../../../../Common/js/common/datacenterData.js";

import MilestoneTrendChart from "./MilestoneTrendChart/MilestoneTrendChart.jsx";
import MilestoneTimelineChart from "./MilestoneTimelineChart/MilestoneTimelineChart.jsx";
import EventList from "./EventList/EventList.jsx";

import useBoundingClientRect from "../../common/useBoundingClientRect.js";

const propTypes = {
  title: string,
  source: string,
  excludeUrl: string,
  characteristics: arrayOf(
    shape({
      title: string,
      events: arrayOf(
        shape({
          year: string,
          title: string,
          description: string, // with html
        }),
      ),
    }),
  ).isRequired,
  results: shape({
    data: arrayOf(indicatorDataShape).isRequired,
    errorMessage: string,
  }),
  colors: arrayOf(string),
  dottedLineForMissingData: bool,
  showEvents: bool,
};

const MilestoneChart = (props) => {
  const {
    title,
    source,
    excludeUrl,
    dottedLineForMissingData,
    showEvents,
    colors,
  } = props;
  const { data } = props.results;

  const [containerDimensions, containerRef] = useBoundingClientRect();

  const svgWidth = containerDimensions.width || 800;

  // space for axis, etc
  const margin = {
    top: 80,
    right: 60,
    bottom: 40,
    left: 60,
  };

  const lineData = R.compose(
    R.head,
    R.values,
    getDataByLocation,
    R.head, // it's assumed that this chart will only ever have 1 dataset
  )(data);

  // add colors to characteristics
  const characteristics = R.map((pair) => {
    const [color, characteristic] = pair;
    return {
      ...characteristic,
      color,
    };
  })(R.zip(colors, props.characteristics));

  const [selectedCharacteristics, setSelectedCharacteristics] = useState(
    R.pluck("title", characteristics),
  );

  // https://til.hashrocket.com/posts/avdtkzbpij-javascript-toggle-item-in-arraysymmetricDifference
  // R.symmetricDifference is _.xor
  const toggleCharacteristic = (title) =>
    setSelectedCharacteristics(
      R.symmetricDifference(selectedCharacteristics, [title]),
    );

  // flatten the characteristics structure in to a flat list of events
  const allEvents = R.chain((c) => {
    return c.events.map((event) => {
      return {
        ...event,
        characteristic: c.title,
        color: c.color,
        isVisible: R.includes(c.title, selectedCharacteristics),
      };
    });
  })(characteristics);

  /* xScale */

  const xScale = useMemo(() => {
    // eg, ["1999", "2020"]
    const eventYears = R.compose(R.pluck("year"), R.defaultTo([]))(allEvents);

    // eg [Date, Date]
    const xDomain = R.compose(
      // show 1 year before and 1 year after extent
      R.over(R.lensIndex(0), addYears(-1)),
      R.over(R.lensIndex(1), addYears(1)),
      R.map(parseISO),
      extent,
      R.concat(eventYears), // include events
      R.pluck("timeFrame"),
      R.prop("dataPoints"),
    )(lineData);

    return scaleTime()
      .range([0, svgWidth - margin.left - margin.right])
      .domain(xDomain);
  }, [allEvents, lineData, margin.left, margin.right, svgWidth]);

  return (
    <div className="milestone-chart" ref={containerRef}>
      <div className="milestone-chart__title title--eyebrow">{title}</div>

      <MilestoneTrendChart
        xScale={xScale}
        lineData={lineData}
        width={svgWidth}
        marginLeft={margin.left}
        excludeUrl={excludeUrl}
        dottedLineForMissingData={dottedLineForMissingData}
      />

      <div className="milestone-chart__help-text">Click to filter</div>
      <div className="milestone-chart__characteristics">
        {characteristics.map((characteristic) => {
          return (
            <button
              type="button"
              className={cx("milestone-chart__characteristic", {
                "milestone-chart__characteristic--selected": R.contains(
                  characteristic.title,
                  selectedCharacteristics,
                ),
              })}
              key={characteristic.color}
              style={{ color: characteristic.color }}
              onClick={(e) => toggleCharacteristic(characteristic.title)}
            >
              {characteristic.title}
            </button>
          );
        })}
      </div>
      <div className="milestone-chart__events">Events</div>
      <div className="milestone-chart__help-text">Click to learn more</div>
      <MilestoneTimelineChart
        xScale={xScale}
        marginLeft={margin.left}
        width={svgWidth}
        events={allEvents}
        colors={colors}
      />

      {source && <div className="milestone-chart__source">{source}</div>}

      {showEvents && <EventList events={allEvents} />}
    </div>
  );
};

MilestoneChart.propTypes = propTypes;
export default MilestoneChart;
